import { useRateFieldError } from "../../hooks/useRateFieldError";
import { FC, useMemo } from "react";

import Grid from "@mui/material/Grid";
import { TicketingFormCategory } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateCategoryNoun } from "../../hooks/useRateCategoryNoun";
import { useRateFieldName } from "../../hooks/useRateFieldName";

import { useFormInputChanged } from "@/common/hooks/useFormInputChanged";
import { Translate, useTranslate } from "@/common/hooks/useTranslate";
import { DashboardRichTextField } from "@/dashboard-design-system/RichTextField";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

const getPlaceholder = (formCategory: TicketingFormCategory, isRegistration: boolean, t: Translate): string => {
  if (formCategory === TicketingFormCategory.Sweepstakes) {
    return t("dashboard", "sweepstakes.rates.descriptionPlaceholder") as string;
  }
  if (formCategory === TicketingFormCategory.Shop) {
    return t("dashboard", "ticketing.rateDescriptionPlaceholderShop") as string;
  }
  if (formCategory === TicketingFormCategory.Custom) {
    return t("dashboard", "ticketing.rateDescriptionPlaceholderCustom") as string;
  }
  if (isRegistration) {
    return t("dashboard", "ticketing.rateDescriptionPlaceholderRegistration") as string;
  }
  if (formCategory === TicketingFormCategory.Auction) {
    return t("dashboard", "ticketing.auctionItemDescriptionPlaceholder") as string;
  }
  if (formCategory === TicketingFormCategory.MembershipV2) {
    return t("dashboard", "ticketing.rateDescriptionPlaceholderMembership") as string;
  }
  return t("dashboard", "ticketing.rateDescriptionPlaceholder") as string;
};

interface DescriptionFieldProps {
  placeholder?: string;
}

export const DescriptionField: FC<DescriptionFieldProps> = ({ placeholder }) => {
  const { t } = useTranslate();
  const { formCategory, isEditing, isOnboarding, isRegistration, alreadyBoughtTicketsCount } = useRateContext();
  const { control, getValues } = useFormContext<StepTicketsFormData>();
  const { formInputChangedCallback } = useFormInputChanged();

  const fieldName = useRateFieldName("description");
  const error = useRateFieldError("description");

  const isAuction = formCategory === TicketingFormCategory.Auction;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultValue = useMemo(() => getValues(fieldName) ?? "", []);

  const alreadyBought = useWatch({ control, name: useRateFieldName("alreadyBoughtTicketsCount") });
  const ticketNumber = alreadyBought ?? alreadyBoughtTicketsCount;

  const attendeesByTicket = useWatch({ control, name: useRateFieldName("attendeesByTicket") });
  const isGroupRate = Boolean(attendeesByTicket && attendeesByTicket > 1);
  const groupCount = isGroupRate ? Math.round(ticketNumber / (attendeesByTicket as number)) : ticketNumber;
  const categoryNoun = useRateCategoryNoun();

  const showsSoldCaption = formCategory === TicketingFormCategory.Auction;

  return (
    <>
      <DashboardRichTextField
        debounced={250}
        data-test="rate-description-input"
        defaultValue={defaultValue}
        name={fieldName}
        label={t("dashboard", "ticketing.rateDescription")}
        control={control}
        placeholder={placeholder ?? getPlaceholder(formCategory, isRegistration, t)}
        errorMessage={error && t("dashboard", `${error.message}` as never)}
        onBlur={formInputChangedCallback({
          input_name: "rate_description",
          form_type: formCategory,
          field_empty: !getValues(fieldName),
          valid_field: !error,
          is_editing: isEditing,
          is_onboarding: isOnboarding,
        })}
        slotProps={{
          content: { sx: { minHeight: isAuction ? 100 : 64 } },
          placeholder: { sx: { right: 8, maxHeight: 96, overflow: "hidden" } },
        }}
      />
      {Boolean(ticketNumber) && showsSoldCaption && (
        <Grid container justifyContent="flex-end" sx={(theme) => ({ color: theme.palette.primary.main })}>
          {isGroupRate
            ? t("dashboard", `donationForm.alreadyBuyGroup.${categoryNoun}` as const, {
                count: groupCount,
                ticketCount: ticketNumber,
              })
            : t("dashboard", "donationForm.alreadyBuyTicket", { ticketNumber })}
        </Grid>
      )}
    </>
  );
};
